import { useEffect, useRef } from 'react';
import Intercom, { boot, shutdown, update } from '@intercom/messenger-js-sdk';
import { useAuthStore } from '@/auth/presentation/store/authStore';
import { useTranslation } from 'react-i18next';
import { appLanguage } from '@shared/infrastructure/i18n/appLanguage';

const INTERCOM_APP_ID = 'o9x7i9xg';

/**
 * Carga el Messenger de Intercom para el usuario autenticado.
 *
 * - Boot la primera vez que hay sesión + hash de Identity Verification.
 * - Update cuando cambian datos del usuario (idioma, nombre).
 * - Shutdown en logout para limpiar la cookie de sesión en dispositivos compartidos.
 *
 * Sin sesión no se carga nada — evita exponer el widget como visitor anónimo.
 */
export const useIntercom = (): void => {
  const isLoggedIn = useAuthStore((state) => state.isLoggedIn);
  const user = useAuthStore((state) => state.user);
  const { i18n } = useTranslation();
  const lang = appLanguage(i18n);
  const initializedRef = useRef(false);

  useEffect(() => {
    // user_hash se calcula sobre el Firestore doc ID (user.userId), no sobre el
    // Firebase UID (user.id). El backend devuelve userId desde /v2/auth/me y firma
    // HMAC(userId). Si falta userId, no podemos arrancar Intercom — Identity
    // Verification fallaría con "Invalid user_hash".
    const intercomUserId = user?.userId;

    if (!isLoggedIn || !user || !user.intercomHash || !intercomUserId) {
      if (initializedRef.current) {
        shutdown();
        initializedRef.current = false;
      }
      return;
    }

    const payload = {
      app_id: INTERCOM_APP_ID,
      region: 'us' as const,
      user_id: intercomUserId,
      name: user.getDisplayName(),
      email: user.email,
      created_at: user.createdAt,
      user_hash: user.intercomHash,
      language_override: lang,
    };

    if (!initializedRef.current) {
      Intercom(payload);
      initializedRef.current = true;
    } else {
      // Reboot completo cuando cambia el usuario; update() en el resto de cambios.
      const previousUserId = (window.intercomSettings as { user_id?: string } | undefined)?.user_id;
      if (previousUserId && previousUserId !== intercomUserId) {
        shutdown();
        boot(payload);
      } else {
        update(payload);
      }
    }
  }, [isLoggedIn, user, lang]);
};
